<template>
  <div>
    <div class="d-flex align-center mb-4">
      <h2 class="font-weight-light">Produkte verwalten</h2>
      <v-spacer></v-spacer>
      <v-btn color="primary" depressed rounded @click="openCreate">
        <v-icon left>mdi-plus</v-icon>
        Neues Produkt
      </v-btn>
    </div>

    <div v-if="loading" class="text-center pa-12">
      <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
    </div>

    <div v-else-if="products.length === 0" class="text-center pa-12 grey--text">
      <v-icon size="64" color="grey darken-1">mdi-package-variant-remove</v-icon>
      <div class="mt-3">Noch keine Produkte erfasst</div>
    </div>

    <v-card v-else dark color="rgba(255,255,255,0.03)" flat>
      <v-data-table dark :headers="headers" :items="products" :items-per-page="50" class="transparent">
        <template v-slot:item.image="{ item }">
          <v-avatar size="40" tile rounded class="my-2">
            <admin-product-thumb :product="item" />
          </v-avatar>
        </template>
        <template v-slot:item.title="{ item }">
          <span class="font-weight-medium">{{ item.title }}</span>
        </template>
        <template v-slot:item.productType="{ item }">
          <v-chip x-small :color="item.productType === 'digital' ? 'primary' : 'blue-grey'" dark>
            <v-icon left x-small>
              {{ item.productType === "digital" ? "mdi-download" : "mdi-package-variant" }}
            </v-icon>
            {{ item.productType === "digital" ? "Download" : "Physisch" }}
          </v-chip>
        </template>
        <template v-slot:item.minMax="{ item }">
          <span v-if="item.productType === 'digital'" class="grey--text caption">–</span>
          <span v-else>{{ item.minQuantity }} – {{ item.maxQuantity }}</span>
        </template>
        <template v-slot:item.images="{ item }">
          <v-chip x-small dark color="blue-grey">
            <v-icon left x-small>mdi-image-multiple</v-icon>
            {{ item.images ? item.images.length : 0 }}
          </v-chip>
        </template>
        <template v-slot:item.studios="{ item }">
          <span v-if="!item.availableInStudios || item.availableInStudios.length === 0" class="caption grey--text">Alle</span>
          <span v-else class="caption">{{ item.availableInStudios.length }} Studios</span>
        </template>
        <template v-slot:item.active="{ item }">
          <v-chip x-small :color="item.active ? 'green' : 'grey'" dark>
            {{ item.active ? "Aktiv" : "Inaktiv" }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn v-if="item.productType === 'digital' && item.file" icon small @click="downloadFile(item)">
            <v-icon small>mdi-download</v-icon>
          </v-btn>
          <v-btn icon small @click="openEdit(item)">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small color="error" @click="confirmDelete(item)">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" v-model="dialogOpen" max-width="700" persistent scrollable>
      <v-card dark>
        <v-card-title>
          <v-icon left>{{ isEditMode ? "mdi-pencil" : "mdi-plus-circle" }}</v-icon>
          {{ isEditMode ? "Produkt bearbeiten" : "Neues Produkt" }}
        </v-card-title>

        <v-divider></v-divider>
        <v-card-text style="max-height: 70vh">
          <v-form ref="productForm" v-model="formValid" class="pt-3">
            <!-- Typ-Auswahl -->
            <div class="mb-4">
              <div class="caption grey--text mb-2">Produkt-Typ</div>
              <v-btn-toggle v-model="form.productType" mandatory dense color="primary" dark>
                <v-btn small value="physical">
                  <v-icon left small>mdi-package-variant</v-icon>
                  Physisch
                </v-btn>
                <v-btn small value="digital">
                  <v-icon left small>mdi-download</v-icon>
                  Download
                </v-btn>
              </v-btn-toggle>
            </div>

            <v-text-field v-model="form.title" label="Titel *" outlined dense dark :rules="[(v) => !!v || 'Pflichtfeld']" />

            <v-textarea v-model="form.description" label="Beschreibung" outlined dense dark rows="4" />

            <v-row v-if="form.productType !== 'digital'" dense>
              <v-col cols="6">
                <v-text-field v-model.number="form.minQuantity" label="Min. Anzahl" type="number" outlined dense dark min="1" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model.number="form.maxQuantity" label="Max. Anzahl" type="number" outlined dense dark min="1" />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-model.number="form.sortOrder"
                  label="Sortierung"
                  type="number"
                  outlined
                  dense
                  dark
                  hint="Kleinere Werte zuerst"
                  persistent-hint />
              </v-col>
              <v-col cols="6" class="d-flex align-center pl-3">
                <v-switch v-model="form.active" label="Aktiv" color="primary" hide-details inset />
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="form.availableInStudios"
                  :items="studioOptions"
                  item-text="name"
                  item-value="_id"
                  label="Verfügbar in Studios"
                  outlined
                  dense
                  dark
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  hint="Leer lassen = in allen Studios verfügbar"
                  persistent-hint
                  class="mt-2" />
              </v-col>
            </v-row>

            <!-- Digital-File-Sektion -->
            <div v-if="form.productType === 'digital'" class="digital-file-section mt-4">
              <div class="caption grey--text mb-2">
                <v-icon small color="primary">mdi-file-pdf-box</v-icon>
                Download-Datei (PDF, max. 25 MB) *
              </div>

              <div v-if="isEditMode && existingDigitalFile && !removeDigitalFile" class="existing-file-card mb-3">
                <v-icon color="primary" class="mr-3">mdi-file-pdf-box</v-icon>
                <div class="flex-grow-1">
                  <div class="font-weight-medium">{{ existingDigitalFile.originalName }}</div>
                  <div class="caption grey--text">
                    {{ formatFileSize(existingDigitalFile.size) }} – hochgeladen {{ $moment(existingDigitalFile.uploadedAt).format("DD.MM.YYYY") }}
                  </div>
                </div>
                <v-btn icon small color="error" @click="removeDigitalFile = true">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>

              <v-file-input
                v-model="form.digitalFile"
                accept="application/pdf,.pdf"
                :label="existingDigitalFile && !removeDigitalFile ? 'Datei ersetzen' : 'PDF hochladen'"
                outlined
                dense
                dark
                show-size
                prepend-icon=""
                prepend-inner-icon="mdi-cloud-upload"
                hint="Wird beim Bestellen direkt zum Download bereitgestellt."
                persistent-hint />
            </div>

            <!-- Bestehende Bilder -->
            <div v-if="isEditMode && existingImages.length > 0" class="mt-4">
              <div class="caption grey--text mb-2">Bestehende Bilder ({{ existingImages.length }})</div>
              <div class="existing-images">
                <div v-for="img in existingImages" :key="img.fileId" class="existing-img-wrap">
                  <img :src="imageUrls[img.fileId]" v-if="imageUrls[img.fileId]" class="existing-img" />
                  <div v-else class="existing-img-loading">
                    <v-progress-circular indeterminate size="20"></v-progress-circular>
                  </div>
                  <v-btn icon small class="remove-img-btn" color="error" @click="markImageForRemoval(img.fileId)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>

            <div class="mt-4">
              <v-file-input
                v-model="form.newImages"
                multiple
                accept="image/*"
                label="Bilder hochladen"
                outlined
                dense
                dark
                show-size
                counter
                prepend-icon=""
                prepend-inner-icon="mdi-image-plus"
                hint="Mehrere Bilder möglich. Max. 10 MB pro Bild."
                persistent-hint />
            </div>

            <v-divider class="my-4" dark></v-divider>

            <!-- Optionen / Spezifikationen (z.B. Farben, Grössen) -->
            <div class="mt-4">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="subtitle-2 font-weight-bold white--text">
                  Optionen / Varianten (z.B. Farben, Grössen)
                </div>
                <v-btn small color="primary" outlined @click="addOption">
                  <v-icon left small>mdi-plus</v-icon> Option hinzufügen
                </v-btn>
              </div>

              <div v-if="form.options && form.options.length > 0" class="options-container">
                <v-card v-for="(opt, optIdx) in form.options" :key="optIdx" class="mb-4 pa-3" color="rgba(255,255,255,0.03)" outlined>
                  <div class="d-flex align-center mb-3">
                    <v-text-field
                      v-model="opt.name"
                      label="Name der Option (z.B. Farbe, Grösse)"
                      outlined
                      dense
                      dark
                      hide-details
                      class="mr-2" />
                    <v-btn icon color="error" small @click="removeOption(optIdx)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>

                  <div class="choices-container pl-4" style="border-left: 2px solid rgba(255,255,255,0.1)">
                    <div class="caption grey--text mb-2">Auswahlwerte (z.B. weiss, rot / S, M, L)</div>
                    <div v-for="(choice, choiceIdx) in opt.choices" :key="choiceIdx" class="d-flex align-center mb-2" style="gap: 8px">
                      <v-text-field
                        v-model="choice.value"
                        label="Wert *"
                        outlined
                        dense
                        dark
                        hide-details
                        style="max-width: 140px"
                        :rules="[(v) => !!v || 'Pflichtfeld']" />

                      <v-select
                        v-model="choice.imageFileId"
                        :items="existingImages"
                        item-text="originalName"
                        item-value="fileId"
                        label="Bild verknüpfen (optional)"
                        outlined
                        dense
                        dark
                        hide-details
                        clearable
                        style="flex: 1">
                        <template v-slot:item="{ item }">
                          <v-avatar size="24" tile class="mr-2 rounded">
                            <img :src="imageUrls[item.fileId]" v-if="imageUrls[item.fileId]" />
                            <v-icon v-else small color="grey">mdi-image</v-icon>
                          </v-avatar>
                          <span class="text-truncate text-caption" style="max-width: 250px">{{ item.originalName }}</span>
                        </template>
                        <template v-slot:selection="{ item }">
                          <v-avatar size="20" tile class="mr-2 rounded" v-if="imageUrls[item.fileId]">
                            <img :src="imageUrls[item.fileId]" />
                          </v-avatar>
                          <span class="text-truncate text-caption" style="max-width: 120px">{{ item.originalName }}</span>
                        </template>
                      </v-select>

                      <v-btn icon color="error" x-small @click="removeChoice(optIdx, choiceIdx)">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>

                    <v-btn small text color="primary" class="mt-1" @click="addChoice(optIdx)">
                      <v-icon left small>mdi-plus</v-icon> Wert hinzufügen
                    </v-btn>
                  </div>
                </v-card>
              </div>
              <div v-else class="caption grey--text text-center pa-4" style="background: rgba(255,255,255,0.01); border: 1px dashed rgba(255,255,255,0.1); border-radius: 8px">
                Keine Optionen definiert (Produkt wird direkt bestellbar sein).
              </div>
            </div>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog" :disabled="saving">Abbrechen</v-btn>
          <v-btn color="primary" depressed :loading="saving" :disabled="!formValid" @click="save">
            {{ isEditMode ? "Speichern" : "Erfassen" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"

const AdminProductThumb = {
  name: "AdminProductThumb",
  props: { product: { type: Object, required: true } },
  data() {
    return { url: null }
  },
  async mounted() {
    const first = this.product.images && this.product.images[0]
    if (!first) return
    try {
      this.url = await api.marketingShop.loadProductImage(this.product._id, first.fileId)
    } catch (e) {
      /* ignore */
    }
  },
  beforeDestroy() {
    if (this.url) URL.revokeObjectURL(this.url)
  },
  template: `
    <div style="width: 100%; height: 100%;">
      <img v-if="url" :src="url" style="width: 100%; height: 100%; object-fit: cover;" />
      <div v-else style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.05);">
        <v-icon small color="grey darken-1">mdi-image-off</v-icon>
      </div>
    </div>`,
}

export default {
  name: "ProductAdmin",
  components: { AdminProductThumb },
  props: {
    products: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    studioOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      dialogOpen: false,
      formValid: false,
      saving: false,
      editingId: null,
      form: this.getEmptyForm(),
      existingImages: [],
      imagesToRemove: [],
      imageUrls: {},
      existingDigitalFile: null,
      removeDigitalFile: false,
      headers: [
        { text: "Bild", value: "image", sortable: false, width: "60px" },
        { text: "Titel", value: "title" },
        { text: "Typ", value: "productType" },
        { text: "Min/Max", value: "minMax", sortable: false },
        { text: "Bilder", value: "images", sortable: false },
        { text: "Sortierung", value: "sortOrder" },
        { text: "Status", value: "active" },
        { text: "Studios", value: "studios", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    }
  },
  computed: {
    isEditMode() {
      return !!this.editingId
    },
  },
  methods: {
    getEmptyForm() {
      return {
        title: "",
        description: "",
        productType: "physical",
        minQuantity: 1,
        maxQuantity: 100,
        sortOrder: 0,
        active: true,
        availableInStudios: [],
        newImages: [],
        digitalFile: null,
        options: [],
      }
    },
    formatFileSize(bytes) {
      if (!bytes) return "0 B"
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`
    },
    openCreate() {
      this.form = this.getEmptyForm()
      this.editingId = null
      this.existingImages = []
      this.imagesToRemove = []
      this.existingDigitalFile = null
      this.removeDigitalFile = false
      this.dialogOpen = true
      this.$nextTick(() => {
        if (this.$refs.productForm) this.$refs.productForm.resetValidation()
      })
    },
    async openEdit(product) {
      this.form = {
        title: product.title || "",
        description: product.description || "",
        productType: product.productType || "physical",
        minQuantity: product.minQuantity || 1,
        maxQuantity: product.maxQuantity || 100,
        sortOrder: product.sortOrder || 0,
        active: product.active !== false,
        availableInStudios: (product.availableInStudios || []).map((s) => (typeof s === "object" ? s._id : s)),
        newImages: [],
        digitalFile: null,
        options: (product.options || []).map((o) => ({
          name: o.name || "",
          choices: (o.choices || []).map((c) => ({
            value: c.value || "",
            imageFileId: c.imageFileId || null,
          })),
        })),
      }
      this.editingId = product._id
      this.existingImages = [...(product.images || [])]
      this.imagesToRemove = []
      this.existingDigitalFile = product.file || null
      this.removeDigitalFile = false
      this.imageUrls = {}
      this.dialogOpen = true

      for (const img of this.existingImages) {
        api.marketingShop
          .loadProductImage(product._id, img.fileId)
          .then((url) => this.$set(this.imageUrls, img.fileId, url))
          .catch(() => {})
      }
    },
    closeDialog() {
      this.dialogOpen = false
      this.editingId = null
      Object.values(this.imageUrls).forEach((u) => URL.revokeObjectURL(u))
      this.imageUrls = {}
    },
    addOption() {
      if (!this.form.options) {
        this.$set(this.form, "options", [])
      }
      this.form.options.push({ name: "", choices: [] })
    },
    removeOption(optIdx) {
      this.form.options.splice(optIdx, 1)
    },
    addChoice(optIdx) {
      this.form.options[optIdx].choices.push({ value: "", imageFileId: null })
    },
    removeChoice(optIdx, choiceIdx) {
      this.form.options[optIdx].choices.splice(choiceIdx, 1)
    },
    markImageForRemoval(fileId) {
      this.imagesToRemove.push(fileId)
      this.existingImages = this.existingImages.filter((i) => i.fileId !== fileId)
      if (this.imageUrls[fileId]) {
        URL.revokeObjectURL(this.imageUrls[fileId])
        this.$delete(this.imageUrls, fileId)
      }
    },
    async downloadFile(product) {
      try {
        const filename = product.file?.originalName || `${product.title}.pdf`
        await api.marketingShop.downloadProductFile(product._id, filename)
      } catch (e) {
        console.error("Download fehlgeschlagen:", e)
        alert("Datei konnte nicht heruntergeladen werden")
      }
    },
    async save() {
      if (!this.$refs.productForm.validate()) return

      if (this.form.productType !== "digital" && this.form.maxQuantity < this.form.minQuantity) {
        alert("Max. Anzahl muss grösser oder gleich Min. Anzahl sein")
        return
      }

      // Digital-Produkt: Datei muss vorhanden sein
      if (this.form.productType === "digital") {
        const hasExisting = this.existingDigitalFile && !this.removeDigitalFile
        const hasNew = !!this.form.digitalFile
        if (!hasExisting && !hasNew) {
          alert("Bei einem Download-Produkt muss eine PDF-Datei hochgeladen werden.")
          return
        }
      }

      this.saving = true
      try {
        const fd = new FormData()
        fd.append("title", this.form.title.trim())
        fd.append("description", this.form.description || "")
        fd.append("productType", this.form.productType || "physical")
        fd.append("minQuantity", this.form.minQuantity)
        fd.append("maxQuantity", this.form.maxQuantity)
        fd.append("sortOrder", this.form.sortOrder)
        fd.append("active", this.form.active)
        fd.append("availableInStudios", (this.form.availableInStudios || []).join(","))
        if (this.imagesToRemove.length > 0) fd.append("removedImageIds", this.imagesToRemove.join(","))
        if (this.form.newImages && this.form.newImages.length > 0) {
          this.form.newImages.forEach((f) => fd.append("images", f))
        }
        if (this.form.digitalFile) fd.append("digitalFile", this.form.digitalFile)
        if (this.removeDigitalFile && !this.form.digitalFile) fd.append("removeDigitalFile", "true")

        if (this.form.options && this.form.options.length > 0) {
          const cleanOptions = this.form.options
            .filter((o) => o.name && o.name.trim())
            .map((o) => ({
              name: o.name.trim(),
              choices: (o.choices || [])
                .filter((c) => c.value && c.value.trim())
                .map((c) => ({
                  value: c.value.trim(),
                  imageFileId: c.imageFileId || null,
                })),
            }))
          fd.append("options", JSON.stringify(cleanOptions))
        } else {
          fd.append("options", JSON.stringify([]))
        }

        if (this.isEditMode) {
          await api.marketingShop.updateProduct(this.editingId, fd)
        } else {
          await api.marketingShop.createProduct(fd)
        }

        this.closeDialog()
        this.$emit("reload")
      } catch (e) {
        console.error("Speichern fehlgeschlagen:", e)
        const msg = e.response?.data?.error || "Fehler beim Speichern"
        alert(msg)
      } finally {
        this.saving = false
      }
    },
    async confirmDelete(product) {
      if (!confirm(`Produkt "${product.title}" wirklich löschen?`)) return
      try {
        await api.marketingShop.deleteProduct(product._id)
        this.$emit("reload")
      } catch (e) {
        console.error("Löschen fehlgeschlagen:", e)
        alert("Fehler beim Löschen")
      }
    },
  },
}
</script>

<style scoped>
.transparent {
  background: transparent !important;
}

.existing-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
}

.existing-img-wrap {
  position: relative;
  aspect-ratio: 1;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  overflow: hidden;
}

.existing-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.existing-img-loading {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-img-btn {
  position: absolute !important;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6) !important;
}

.digital-file-section {
  background: rgba(0, 150, 64, 0.05);
  border: 1px solid rgba(0, 150, 64, 0.2);
  border-radius: 8px;
  padding: 14px;
}

.existing-file-card {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  padding: 12px;
}
</style>
